import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { money, compactMoney, pct } from "../lib/format";
import ProgramFinancials from "../components/ProgramFinancials";
import { monthLabel } from "../lib/quarters";

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };
const item = { hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0, transition: { duration: 0.35 } } };

function StatCell({ label, value, sub, big, testId, span = "lg:col-span-2" }) {
  return (
    <motion.div variants={item} className={`grid-cell p-6 ${span}`} data-testid={testId}>
      <div className="eyebrow">{label}</div>
      <div className={`stat-num mt-3 ${big ? "text-5xl md:text-6xl" : "text-3xl md:text-4xl"}`}>
        {value}
      </div>
      {sub && <div className="text-sm mt-3" style={{ color: "var(--muted)" }}>{sub}</div>}
    </motion.div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/summary");
      setSummary(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const s = summary || {};
  const term = s.surplus_word === "Surplus" ? "Surplus" : "ERRs";
  const termSingular = s.surplus_word === "Surplus" ? "Surplus" : "ERR";
  const invoiceService = user?.invoice_service !== false;

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10 md:py-14">
      <motion.div initial="hidden" animate="show" variants={stagger}>
        <motion.div variants={item} className="flex flex-col gap-6 border-b pb-8 md:flex-row md:items-end md:justify-between" style={{ borderColor: "var(--border)" }}>
          <div>
            <div className="eyebrow mb-2">Employer dashboard · {user?.company}</div>
            <h1 className="font-heading font-black text-5xl md:text-6xl leading-none tracking-tight" style={{ color: "var(--ink)" }}>
              Hello, {user?.name?.split(" ")[0]}
            </h1>
            <p className="mt-3 text-[15px] max-w-lg" style={{ color: "var(--muted)" }}>
              Here's the live posture of your captive insurance program and the earned refunds available to your organization.
            </p>
          </div>
          {invoiceService && (
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/invoices" className="btn-primary flex items-center gap-2" data-testid="btn-submit-invoice">
                Submit Invoice <ArrowRight size={16} />
              </Link>
            </div>
          )}
        </motion.div>

        <motion.div variants={item} className="mt-6 grid grid-cols-2 border md:grid-cols-4" style={{ borderColor: "var(--border)", background: "#F3F3F1" }} data-testid="program-context-bar">
          <ContextCell label="Employer" value={user?.company || "—"} testId="program-context-client" />
          <ContextCell label="Program start date" value={s.program_start || "—"} testId="program-context-policy-end" />
          {invoiceService
            ? <ContextCell label="Invoice activity" value={`${s.invoices_count || 0} submitted`} testId="program-context-invoices" />
            : <ContextCell label="Invoice payments" value="Not included" testId="program-context-invoices" />}
          <ContextCell label="Data through" value={s.data_through ? monthLabel(s.data_through) : "—"} testId="program-context-payout" />
        </motion.div>

        <div className={`mt-6 grid gap-8 ${s.ppr_enabled ? "lg:grid-cols-2" : "grid-cols-1"}`}>
          <div>
            <div className="border grid-borders grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="kpi-grid">
              <StatCell label={`Available ${term}`} value={compactMoney(s.err_available)} sub={money(s.err_available) + " authorized for use"} big span="lg:col-span-12" testId="kpi-available" />
              <StatCell label={`Total ${termSingular} Balance`} value={compactMoney(s.err_balance)} sub="Program to date" span={invoiceService ? "lg:col-span-4" : "lg:col-span-6"} testId="kpi-balance" />
              <StatCell label="Paid out" value={compactMoney(s.err_used)} sub="Program to date" span={invoiceService ? "lg:col-span-4" : "lg:col-span-6"} testId="kpi-paid" />
              {invoiceService && (
                <StatCell label="Pending" value={compactMoney(s.err_pending)} sub={`${s.invoices_pending || 0} awaiting settlement`} span="lg:col-span-4" testId="kpi-pending" />
              )}
            </div>
            <UtilizationCard
              eyebrow={`${term} utilization`}
              heading={`Where your ${term} stand`}
              balance={s.err_balance}
              used={s.err_used}
              pending={s.err_pending}
              available={s.err_available}
              pool="ERR"
              testId="utilization-card"
            />
          </div>

          {s.ppr_enabled && (
            <div>
              <div className="border grid-borders grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="ppr-grid">
                <StatCell label="Available PPRs" value={compactMoney(s.ppr_available)} sub={money(s.ppr_available) + " authorized for use"} big span="lg:col-span-12" testId="kpi-ppr-available" />
                <StatCell label="Total PPR Balance" value={compactMoney(s.ppr_balance)} sub="Program to date" span={invoiceService ? "lg:col-span-4" : "lg:col-span-6"} testId="kpi-ppr-balance" />
                <StatCell label="Paid out" value={compactMoney(s.ppr_used)} sub="Program to date" span={invoiceService ? "lg:col-span-4" : "lg:col-span-6"} testId="kpi-ppr-paid" />
                {invoiceService && (
                  <StatCell label="Pending" value={compactMoney(s.ppr_pending)} sub="Awaiting settlement" span="lg:col-span-4" testId="kpi-ppr-pending" />
                )}
              </div>
              <UtilizationCard
                eyebrow="PPRs utilization"
                heading="Where your PPRs stand"
                balance={s.ppr_balance}
                used={s.ppr_used}
                pending={s.ppr_pending}
                available={s.ppr_available}
                pool="PPR"
                testId="ppr-utilization-card"
              />
            </div>
          )}
        </div>

        <motion.div variants={item} className="mt-8 border p-8" style={{ borderColor: "var(--border)", background: "var(--ink)", color: "#F7F7F6" }} data-testid="program-card">
          <div className="eyebrow" style={{ color: "#87BBA7" }}>Program status</div>
          <div className="font-heading font-black text-3xl mt-3 leading-none">Earned &amp; Authorized</div>
          <div className="mt-6 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
            <Row k="Contributed YTD" v={money(s.ytd_contributed)} />
            <Row k="Program started" v={s.program_start || "—"} />
            {invoiceService && <Row k="Invoices submitted" v={s.invoices_count || 0} />}
          </div>
        </motion.div>

        {/* Program financials */}
        <ProgramFinancials />
      </motion.div>

    </div>
  );
}

function UtilizationCard({ eyebrow, heading, balance, used, pending, available, pool, testId }) {
  const usedPct = balance ? Math.min((used / balance) * 100, 100) : 0;
  const pendingPct = balance ? Math.min((pending / balance) * 100, 100 - usedPct) : 0;
  const [open, setOpen] = useState(false);
  const [trail, setTrail] = useState(null);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !trail) {
      try {
        const { data } = await api.get(`/dashboard/payment-trail?pool=${pool}`);
        setTrail(data);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }
  };

  return (
    <motion.div variants={item} className="mt-6 border p-8" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="eyebrow">{eyebrow}</div>
          <div className="font-heading font-bold text-2xl mt-1" style={{ color: "var(--ink)" }}>{heading}</div>
        </div>
        <div className="stat-num text-3xl" data-testid={`${testId}-pct`}>{pct(usedPct + pendingPct)}</div>
      </div>
      <div className="relative h-3 w-full" style={{ background: "#EFEEE9" }}>
        <div className="absolute left-0 top-0 h-3" style={{ width: `${usedPct}%`, background: "#003A70" }} />
        <div className="absolute top-0 h-3" style={{ left: `${usedPct}%`, width: `${pendingPct}%`, background: "#009ADE" }} />
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 text-sm sm:grid-cols-3">
        <Legend swatch="#003A70" label="Paid" value={money(used)} />
        <Legend swatch="#009ADE" label="Pending" value={money(pending)} />
        <Legend swatch="#EFEEE9" label="Available" value={money(available)} />
      </div>

      <button
        onClick={toggle}
        className="mt-6 flex w-full items-center justify-between border-t pt-4 text-left text-sm transition-colors hover:opacity-70"
        style={{ borderColor: "var(--border)", color: "var(--ink)" }}
        data-testid={`${testId}-trail-toggle`}
      >
        <span className="font-semibold">What made up the {money(used)} paid out</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-4" data-testid={`${testId}-trail`}>
          {!trail ? (
            <div className="text-sm" style={{ color: "var(--muted)" }}>Loading payments…</div>
          ) : !trail.entries.length ? (
            <div className="text-sm" style={{ color: "var(--muted)" }} data-testid={`${testId}-trail-empty`}>
              {used > 0
                ? `The ${money(used)} paid out comes from totals your carrier reported for the program; individual payments have not been itemised in the portal feed yet.`
                : "No individual payments have been reported against this fund yet."}
            </div>
          ) : (
            <>
              <div className="max-h-96 overflow-auto" data-testid={`${testId}-trail-scroll`}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="eyebrow text-[10px]" style={{ color: "var(--muted)" }}>
                      <th className="py-2 pr-4 text-left font-normal">Paid</th>
                      <th className="py-2 pr-4 text-left font-normal">Payee</th>
                      <th className="py-2 pr-4 text-left font-normal">Reference</th>
                      <th className="py-2 pr-4 text-left font-normal">Source</th>
                      <th className="py-2 text-right font-normal">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trail.entries.map((entry, index) => (
                      <tr key={`${entry.reference}-${index}`} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`${testId}-trail-row-${index}`}>
                        <td className="py-2 pr-4 whitespace-nowrap" style={{ color: "var(--muted)" }}>{entry.date || "—"}</td>
                        <td className="py-2 pr-4" style={{ color: "var(--ink)" }}>{entry.payee || "—"}</td>
                        <td className="py-2 pr-4" style={{ color: "var(--muted)" }}>{entry.reference || "—"}</td>
                        <td className="py-2 pr-4" style={{ color: "var(--muted)" }}>{entry.source}</td>
                        <td className="py-2 text-right font-semibold" style={{ color: "var(--ink)" }}>{money(entry.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2" style={{ borderColor: "var(--ink)" }}>
                      <td className="py-2 pr-4 font-semibold" colSpan={4} style={{ color: "var(--ink)" }}>
                        {trail.entries.length} payment{trail.entries.length === 1 ? "" : "s"}
                      </td>
                      <td className="py-2 text-right font-bold" style={{ color: "var(--ink)" }} data-testid={`${testId}-trail-total`}>{money(trail.total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {Math.abs(trail.total - used) > 1 && (
                <div className="mt-3 text-xs" style={{ color: "var(--muted)" }} data-testid={`${testId}-trail-note`}>
                  Itemised payments total {money(trail.total)} against a reported {money(used)} paid out.
                  Any difference is activity your carrier has settled but not yet itemised in the portal feed.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </motion.div>
  );
}

function Legend({ swatch, label, value }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="inline-block w-3 h-3" style={{ background: swatch, border: swatch === "#EFEEE9" ? "1px solid var(--border-strong)" : "none" }} />
      <div>
        <div className="eyebrow text-[9px]">{label}</div>
        <div className="font-heading font-bold" style={{ color: "var(--ink)" }}>{value}</div>
      </div>
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between opacity-90">
      <span className="eyebrow" style={{ color: "#87BBA7" }}>{k}</span>
      <span className="font-heading font-bold">{v}</span>
    </div>
  );
}
export function StatusPill({ status }) {
  const map = {
    paid: { cls: "pill-success", label: "Paid" },
    approved: { cls: "pill pill-solid", label: "Approved" },
    pending: { cls: "pill pill-warn", label: "Pending" },
    rejected: { cls: "pill-danger", label: "Rejected" },
  };
  const m = map[status] || { cls: "pill", label: status };
  return <span className={`pill ${m.cls}`}>{m.label}</span>;
}

function ContextCell({ label, value, testId }) {
  return (
    <div className="border-r p-4 last:border-r-0" style={{ borderColor: "var(--border)" }} data-testid={testId}>
      <div className="eyebrow text-[9px]">{label}</div>
      <div className="mt-1 truncate font-heading text-sm font-bold" style={{ color: "var(--ink)" }}>
        {value}
      </div>
    </div>
  );
}
